<template>
  <div
    :data-qa="dataQa ? `${dataQa}_modal` : 's-modal'">
    <slot
      name="activator"
      :visible="visible"/>
    <transition name="s-fade-in-linear">
      <div
        v-if="mask && visible"
        key="mask"
        :class="maskClasses"
        :data-modal-id="modalId"
        :data-qa="dataQa ? `${dataQa}_mask` : 's-modal-mask'"
        :style="wrapStyles"
        @click="handleMask"/>
    </transition>
    <div
      :class="wrapClasses"
      :data-modal-id="modalId"
      :data-qa="dataQa ? `${dataQa}_wrap` : 's-modal-wrap'"
      :style="wrapStyles"
      @click="handleWrapClick">
      <transition
        name="s-zoom-in-center"
        @after-leave="animationFinish">
        <div
          v-if="visible"
          :key="`modal-${modalId}`"
          :class="classes"
          :data-qa="dataQa ? `${dataQa}_main` : 's-modal-main'"
          :is-active="visible"
          :style="mainStyles"
          @mousedown="handleMousedown">
          <div
            ref="content"
            :class="contentClasses"
            :data-qa="dataQa ? `${dataQa}_content` : 's-modal-content'"
            :style="contentStyle">
            <div
              v-if="showHead"
              :class="[prefixCls + '-header secondary__green_chosen']"
              :data-qa="dataQa ? `${dataQa}_header` : 's-modal-header'">
              <slot name="header">
                <div
                  v-dompurify-html="title"
                  :class="[prefixCls + '-header-inner']"
                  :data-qa="dataQa ? `${dataQa}_title` : 's-modal-title'"/>
              </slot>
              <div
                v-if="closable"
                :class="[prefixCls + '-close']">
                <sButton
                  :data-qa="dataQa ? `${dataQa}_btn-close-dialog` : 'btn-close-dialog'"
                  icon="cross"
                  type="text"
                  @click="close"/>
              </div>
            </div>
            <div
              :class="[prefixCls + '-body', {'no-head': !showHead && !$slots.header}]"
              :data-qa="dataQa ? `${dataQa}_body` : 's-modal-body'"
              :style="bodyStyle">
              <slot/>
            </div>
            <transition-group
              v-if="!footerHide"
              :class="[prefixCls + '-footer']"
              :data-qa="dataQa ? `${dataQa}_footer` : 's-modal-footer'"
              name="s-fade-in-linear"
              tag="div">
              <slot name="footer">
                <sButton
                  key="btn-ok"
                  :data-qa="dataQa ? `${dataQa}_btn-ok` : 'btn-ok'"
                  :loading="buttonLoading"
                  @click="ok">
                  {{ okLabel }}
                </sButton>
                <sButton
                  key="btn-cancel"
                  :data-qa="dataQa ? `${dataQa}_btn-cancel` : 'btn-cancel'"
                  :disabled="buttonLoading"
                  outlined
                  type="text"
                  @click="cancel">
                  {{ cancelLabel }}
                </sButton>
              </slot>
            </transition-group>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
<script>
import Vue from 'vue'
import { findComponentsDownward, off, getMaxZIndex } from 'toolkit/utils/dom'
import Emitter from 'toolkit/mixins/emitter'
import ScrollbarMixins from 'toolkit/mixins/scrollbar'
import { i18n } from '../../utils/i18n.js'
import { eventBus } from '@/main'

Vue.use(i18n)

import {
  transferIndex as modalIndex,
  lastVisibleIndex, lastVisibleIncrease
} from 'toolkit/utils/transfer-queue'
import sButton from 'toolkit/components/button.vue'

const prefixCls = 's-modal'
/**
 * Компонент "Модальное окно"
 * @displayName Modal
 */
export default {
  name: 'sModal',
  mixins: [Emitter, ScrollbarMixins],
  data() {
    this.$i18n.locale = localStorage.language
    return {
      prefixCls,
      wrapShow: false,
      showHead: true,
      buttonLoading: false,
      visible: this.value,
      maskClasses: '',
      dragData: {
        x: null,
        y: null,
        dragX: null,
        dragY: null,
        dragging: false,
      },
      modalIndex: getMaxZIndex(modalIndex),
      isMouseTriggerIn: false,
      useFullScreen: false,
      modalId: `modal-${Math.random().toString(36).substr(2, 9)}-${Date.now()}`,
      focusTrapAttached: false,
    }
  },
  methods: {
    transferToBody() {
      if (!this.transfer) {
        return
      }

      const modalElements = document.querySelectorAll(`[data-modal-id="${this.modalId}"]`)
      
      modalElements.forEach(element => {
        if (element && element.parentNode !== document.body) {
          document.body.appendChild(element)
        }
      })
    },
    checkFullScreen() {
      if (this.fullscreen === true) {
        return
      }
      if (!this.autoFullScreen || !this.$refs.content || !this.$refs.content.parentElement) {
        return
      }
      const parrentElement = this.$refs.content.parentElement
      const heightModal = parrentElement.offsetHeight + parrentElement.offsetTop
      const widthModal = parrentElement.offsetWidth + 2 * parrentElement.offsetLeft
      this.useFullScreen = widthModal > screen.width || heightModal > screen.height
    },
    show() {
      this.changeVisible(true)
    },
    close() {
      this.changeVisible(false)
      this.$emit('input', false)
      this.$emit('close')
    },
    handleMask() {
      if (this.maskClosable && this.mask) {
        this.close()
      }
    },
    handleWrapClick(event) {
      if (this.isMouseTriggerIn) {
        this.isMouseTriggerIn = false
        return
      }
      const className = event.target.getAttribute('class')
      if (className && className.indexOf(`${prefixCls}-wrap`) > -1) { this.handleMask() }
    },
    handleMousedown() {
      this.isMouseTriggerIn = true
    },
    cancel() {
      this.$emit('on-cancel')
      this.close()
    },
    ok() {
      if (this.loading) {
        this.buttonLoading = true
        return
      }
      this.changeVisible(false)
      this.$emit('input', false)
      this.$emit('on-ok')
    },
    escClose(e) {
      if (this.visible && this.closable) {
        if (e.keyCode === 27) {
          const $Modals = findComponentsDownward(this.$root, 'sModal').filter((item) => item.$data.visible && item.$props.closable)
          const $TopModal = $Modals.sort((a, b) => (a.$data.modalIndex < b.$data.modalIndex ? 1 : -1))[0]
          setTimeout(() => {
            $TopModal.close()
          }, 0)
        }
      }
    },
    animationFinish() {
      this.$emit('on-hidden')
    },
    handleMoveMove(event) {
      if (!this.dragData.dragging) { return }
      const distance = {
        x: event.clientX,
        y: event.clientY,
      }
      const diffDistance = {
        x: distance.x - this.dragData.dragX,
        y: distance.y - this.dragData.dragY,
      }
      this.dragData.x += diffDistance.x
      this.dragData.y += diffDistance.y
      this.dragData.dragX = distance.x
      this.dragData.dragY = distance.y
    },
    handleMoveEnd() {
      this.dragData.dragging = false
      off(window, 'mousemove', this.handleMoveMove)
      off(window, 'mouseup', this.handleMoveEnd)
    },
    handleClickModal() {
      if (this.draggable) {
        if (lastVisibleIndex !== this.lastVisibleIndex) {
          this.lastVisibleIndex = lastVisibleIndex
          return
        }
        this.modalIndex = getMaxZIndex(modalIndex)
      }
    },
    changeVisible(state) {
      this.visible = state
      if (state === false) {
        this.timer = setTimeout(() => {
          this.wrapShow = false
          this.removeScrollEffect()
        }, 300)
      } else {
        this.modalIndex = getMaxZIndex(modalIndex)
        if (this.timer) { clearTimeout(this.timer) }
        this.wrapShow = true
        if (!this.scrollable) {
          this.addScrollEffect()
        }
        this.$nextTick(() => {
          this.transferToBody()
        })
      }
      this.visible = state
      this.broadcast('Table', 'on-visible-change', state)
      this.broadcast('Slider', 'on-visible-change', state)
      this.$emit('on-visible-change', state)
    },
    focusTrapHandler(e) {
      if (!this.visible || !this.focusTrapAttached) { return }
      // Из-за асинхронности sModal-enableFocusTrap/-disableFocusTrap возможна гонка,
      // когда ловушка фокуса оказывается одновременно навешана на фоновую модалку
      // (см. modal.vue:389-407). Поэтому даже если ловушка сработала на фоновой
      // модалке, реагирует только самая верхняя видимая модалка с retainFocus.
      const $Modals = findComponentsDownward(this.$root, 'sModal').filter((item) => item.$data.visible && item.$props.retainFocus)
      const $TopModal = $Modals.sort((a, b) => (a.$data.modalIndex < b.$data.modalIndex ? 1 : -1))[0]
      if ($TopModal !== this) { return }
      // Ищем wrap-элемент модалки по data-modal-id и классу s-modal-wrap
      const modals = document.querySelectorAll('[data-modal-id="' + this.modalId + '"]')
      let modalWrap = null
      modals.forEach(el => {
        if (el.classList && el.classList.contains('s-modal-wrap')) {
          modalWrap = el
        }
      })
      if (!modalWrap) { return }
      if (!modalWrap.contains(e.target)) {
        const focusable = modalWrap.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
        if (focusable.length && document.activeElement !== focusable[0]) {
          setTimeout(() => {
            focusable[0].focus()
          }, 0)
        }
      }
    },
    addFocusTrapListener() {
      if (this.retainFocus && this.visible && !this.focusTrapAttached) {
        document.addEventListener('focusin', this.focusTrapHandler)
        this.focusTrapAttached = true
      }
    },
    removeFocusTrapListener() {
      if (this.focusTrapAttached) {
        document.removeEventListener('focusin', this.focusTrapHandler)
        this.focusTrapAttached = false
      }
    },
  },
  computed: {
    valueMutable: {
      get: function () {
        return this.value
      },
      set: function (newValue) {
        this.$emit('update:value', newValue)
        this.visible = newValue
      }
    },
    okLabel() {
      if (this.okText) { return this.okText }
      if (this.confirm) {
        return this.$t('yes')
      }
      return this.$t('ok')
    },
    cancelLabel() {
      if (this.cancelText) { return this.cancelText }
      if (this.confirm) {
        return this.$t('no')
      }
      return this.$t('cancel')
    },
    wrapClasses() {
      return [
        `${prefixCls}-wrap`,
        {
          [`${prefixCls}-hidden`]: !this.wrapShow,
          [`${this.className}`]: !!this.className,
          [`${prefixCls}-no-mask`]: !this.mask,
        },
      ]
    },
    wrapStyles() {
      return {
        zIndex: this.modalIndex + this.zIndex,
        display: this.wrapShow && this.visible ? 'block' : 'none',
      }
    },
    classes() {
      return [
        `${prefixCls}`,
        {
          [`${prefixCls}-fullscreen`]: this.fullscreen || this.useFullScreen,
          [`${prefixCls}-fullscreen-no-header`]: (this.fullscreen || this.useFullScreen) && !this.showHead,
          [`${prefixCls}-fullscreen-no-footer`]: (this.fullscreen || this.useFullScreen) && this.footerHide,
        },
      ]
    },
    contentClasses() {
      return [
        `${prefixCls}-content`,
        this.contentClassName,
        {
          [`${prefixCls}-content-no-mask`]: !this.mask,
        },
      ]
    },
    mainStyles() {
      const style = {}
      if (typeof this.width === 'string' && (this.width.endsWith('%') || this.width.endsWith('px') || ['fit-content', 'auto', 'min-content', 'max-content'].includes(this.width))) {
        style.width = this.width
      } else {
        const width = parseInt(this.width, 10)
        style.width = width <= 100 ? `${width}%` : `${width}px`
      }
      const styleWidth = this.dragData.x !== null ? { top: 0 } : {}
      const customStyle = this.styles ? this.styles : {}
      Object.assign(style, styleWidth, customStyle)
      return style
    },
  },
  watch: {
    valueMutable(val) {
      this.visible = val
    },
    visible(val) {
      if (val === false) {
        this.buttonLoading = false
        this.timer = setTimeout(() => {
          this.wrapShow = false
          this.removeScrollEffect()
        }, 300)
        this.removeFocusTrapListener()
        setTimeout(() => {
          window.dispatchEvent(new Event('sModal-enableFocusTrap'))
        }, 0)
      } else {
        if (this.lastVisible !== val) {
          this.modalIndex = getMaxZIndex(modalIndex)
          lastVisibleIncrease()
        }
        if (this.timer) { clearTimeout(this.timer) }
        this.wrapShow = true
        if (!this.scrollable) {
          this.addScrollEffect()
        }
        this.$nextTick(() => {
          this.transferToBody()
        })
        window.dispatchEvent(new Event('sModal-disableFocusTrap'))
        this.addFocusTrapListener()
      }
      this.broadcast('Table', 'on-visible-change', val)
      this.broadcast('Slider', 'on-visible-change', val)
      this.$emit('on-visible-change', val)
      eventBus.$emit('modalState', val)
      this.lastVisible = val
      this.lastVisibleIndex = lastVisibleIndex
    },
    retainFocus(val) {
      if (val && this.visible) {
        this.addFocusTrapListener()
      } else {
        this.removeFocusTrapListener()
      }
    },
    loading(val) {
      if (!val) {
        this.buttonLoading = false
      }
    },
    scrollable(val) {
      if (!val) {
        this.addScrollEffect()
      } else {
        this.removeScrollEffect()
      }
    },
    title(val) {
      if (this.$slots.header === undefined) {
        this.showHead = !!val
      }
    },
  },
  components: {
    sButton,
  },
  mounted() {
    let showHead = true
    this.maskClasses = `${prefixCls}-mask`
    if (this.$slots.header === undefined && !this.title && !this.closable) {
      showHead = false
    }
    this.showHead = showHead
    document.addEventListener('keydown', this.escClose)
    if (this.autoFullScreen) {
      this.checkFullScreen()
    }

    // Инициализация модального окна при монтировании
    if (this.visible) {
      this.wrapShow = true
      this.modalIndex = getMaxZIndex(modalIndex)
      lastVisibleIncrease()
      
      if (!this.scrollable) {
        this.addScrollEffect()
      }
      
      this.$nextTick(() => {
        this.transferToBody()
      })
      
      this.broadcast('Table', 'on-visible-change', true)
      this.broadcast('Slider', 'on-visible-change', true)
      this.$emit('on-visible-change', true)
      eventBus.$emit('modalState', true)
    }
    this.addFocusTrapListener()
    window.addEventListener('sModal-disableFocusTrap', this.removeFocusTrapListener)
    window.addEventListener('sModal-enableFocusTrap', this.addFocusTrapListener)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.escClose)
    this.removeFocusTrapListener()
    window.removeEventListener('sModal-disableFocusTrap', this.removeFocusTrapListener)
    window.removeEventListener('sModal-enableFocusTrap', this.addFocusTrapListener)
    this.removeScrollEffect()

    if (this.transfer) {
      const modalElements = document.querySelectorAll(`[data-modal-id="${this.modalId}"]`)
      modalElements.forEach(element => {
        if (element && element.parentNode) {
          element.parentNode.removeChild(element)
        }
      })
    }
  },
  props: {
    /**
     * Отображение модалки
     */
    value: {
      type: Boolean,
      default: false,
    },
    /**
     * Отображать кнопку закрытия в правом верхнем углу или нет. ESC нажатие и закрытие также будет отключено, если для параметра closable установлено значение false.
     */
    closable: {
      type: Boolean,
      default: true,
    },
    /**
     * Разрешить операцию закрытия маски (щелчек по маске вокруг модального окна, чтобы закрыть его) или нет.
     */
    maskClosable: {
      type: Boolean,
      default: true,
    },
    /**
     * Название Modal. Недопустимо, если установлен слот заголовка.
     */
    title: {
      type: String,
    },
    /**
     * Ширина адаптивна: она изменится на авто, когда размер экрана меньше 768 пикселей. Если значение меньше 100, оно будет отображаться в процентах, в противном случае это пиксель.
     */
    width: {
      type: [Number, String],
      default: 736,
    },
    /**
     * Текст кнопки ОК.
     */
    okText: {
      type: String,
    },
    /**
     * Текст кнопки отмены.
     */
    cancelText: {
      type: String,
    },
    /**
     * Показывать статус загрузки или нет при нажатии кнопки подтверждения. Если установлено значение true, Modal необходимо закрыть вручную, установив значение false.
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Задайте стиль слоя или отрегулируйте его положение. Применяется к .s-modal.
     */
    styles: {
      type: Object,
      default() {
        return {}
      },
    },
    /**
     * Доп класс для .s-modal-wrap' можно использовать для реализации нестандартных стилей, таких как вертикальный центр.
     */
    className: {
      type: String,
      default: ''
    },
    /**
     * Доп класс для content
     * @ignore
     */
    contentClassName: {
      type: String,
      default: ''
    },
    contentStyle: {
      type: String,
      default: ''
    },
    bodyStyle: {
      type: String,
      default: ''
    },
    /**
     * Скрыть нижний колонтитул.
     */
    footerHide: {
      type: Boolean,
      default: false,
    },
    /**
     * Разрешить прокрутку или нет.
     */
    scrollable: {
      type: Boolean,
      default: false,
    },
    /**
     * Добавлять ли слой в тело.
     */
    transfer: {
      type: Boolean,
      default: true,
    },
    /**
     * полноэкранный режим. Если контент больше экрана
     */
    fullscreen: {
      type: Boolean,
      default: false,
    },
    /**
     * Отображать ли слой маски, по умолчанию не отображать.
     */
    mask: {
      type: Boolean,
      default: true,
    },
    /**
     * Уровень
     */
    zIndex: {
      type: Number,
      default: 1000,
    },
    /**
     * Автоматически переводить в полноэкранный режим
     */
    autoFullScreen: {
      type: Boolean,
      default: false,
    },
    confirm: {
      type: Boolean,
      default: false,
    },
    dataQa: String,
    /**
     * Включать ли ловушку фокуса внутри модалки
     */
    retainFocus: {
      type: Boolean,
      default: true
    },
  },
  i18n: {
    messages: {
      uk: {
        ok: 'OK',
        yes: 'Так',
        no: 'Ні',
        cancel: 'Відминити'
      },
      ru: {
        ok: 'OK',
        yes: 'Да',
        no: 'Нет',
        cancel: 'Отменить'
      },
      en: {
        ok: 'OK',
        yes: 'Yes',
        no: 'No',
        cancel: 'Cancel'
      },
    }
  },
}
</script>
